import { useAuth } from "../../auth";
import { gql, useQuery } from "@apollo/client";
import { clamp } from "lodash";
import {
  Spinner,
  P,
  ProgressBar,
  ButtonLink,
  Panel,
} from "../../ui";
import {
  trackUpgradeCtaClicked,
  plansUrlWithUpgradeSource,
} from "../../analytics";

const ACCOUNT_PLAN_QUERY = gql`
  query AccountPlan($accountId: ID) {
    getAccount {
      id
      name
      plan
      hasBilling
      billingInterval
      usedAiCredits
      sendCount
      livePreviewCount
      maxLivePreviewCredits
      totalTeamMembers
      exportCount
      maxExports
    }
    listRecipients(accountId: $accountId) {
      data {
        id
        email
        status
        verifiedAt
        createdAt
        updatedAt
      }
    }
    listTemplates(accountId: $accountId) {
      data {
        name
        id
        updatedAt
        shareId
      }
    }
  }
`;

// Internal DB keys (PRO, TEAM) mapped to user-facing names
export const planDisplayNames = {
  FREE: 'Free',
  PRO: 'Solo',
  TEAM: 'Pro',
  ENTERPRISE: 'Enterprise',
};

export const Plans = {
  FREE: {
    cost: "$0",
    yearlyCost: "$0",
    templates: 3,
    recipients: 100,
    members: 1,
    quota: 10,
    aiCredits: 2,
    exports: 10,
    livePreviewCredits: 0,
  },
  PRO: {
    cost: "$29",
    yearlyCost: "$290",
    templates: 30,
    recipients: 100,
    members: 1,
    quota: 100,
    aiCredits: 50,
    exports: -1,
    livePreviewCredits: 40,
  },
  TEAM: {
    cost: "$79",
    yearlyCost: "$790",
    templates: 200,
    recipients: 100,
    members: 5,
    quota: 500,
    aiCredits: 250,
    exports: -1,
    livePreviewCredits: 150,
  },
  ENTERPRISE: {
    cost: "Custom",
    yearlyCost: "Custom",
    templates: -1,
    recipients: -1,
    members: -1,
    quota: -1,
    aiCredits: -1,
    exports: -1,
    livePreviewCredits: -1,
  },
};

const formatCurrentPlanLabel = (
  plan: string,
  billingInterval?: string | null
) => {
  const name = planDisplayNames[plan] || plan;
  const planInfo = Plans[plan] || Plans.FREE;

  if (plan === "FREE") {
    return `Current Plan: ${name} (${planInfo.cost})`;
  }
  if (plan === "ENTERPRISE" || planInfo.cost === "Custom") {
    return `Current Plan: ${name} (Custom)`;
  }
  // Don't assume monthly when billingInterval is missing (stale Apollo cache
  // from other getAccount queries that omit the field).
  if (billingInterval === "yearly") {
    return `Current Plan: ${name} (${planInfo.yearlyCost}/yr)`;
  }
  if (billingInterval === "monthly") {
    return `Current Plan: ${name} (${planInfo.cost}/mo)`;
  }
  return `Current Plan: ${name}`;
};

const AccountPlan = () => {
  const { user } = useAuth();
  const { loading, data } = useQuery(ACCOUNT_PLAN_QUERY, {
    ssr: false,
    skip: !user,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
  const isLoading = !data?.listTemplates?.data && (!user || loading);
  const account = data?.getAccount || {};
  const templates = data?.listTemplates?.data || [];
  const recipients = data?.listRecipients?.data || [];
  const sendCount = account?.sendCount || 0;
  const plan = account?.plan || "FREE";
  const livePreviewCount = account?.livePreviewCount || 0;
  const maxLivePreviewCredits =
    account?.maxLivePreviewCredits ?? Plans[plan]?.livePreviewCredits ?? 0;
  const previewProgressBar = clamp(
    Plans[plan].quota === -1 ? 0 : (sendCount / Plans[plan].quota) * 100,
    0,
    100
  );
  const previewDescription =
    Plans[plan].quota === -1
      ? `${sendCount} used (Unlimited)`
      : `${sendCount}/${Plans[plan].quota}`;
  const isUnlimitedTemplates = Plans[plan].templates === -1;
  const templateProgressBar = isUnlimitedTemplates
    ? 0
    : clamp((templates.length / Plans[plan].templates) * 100, 0, 100);
  const templateDescription = isUnlimitedTemplates
    ? `${templates.length} used (Unlimited)`
    : `${templates.length}/${Plans[plan].templates}`;
  const usedAiCredits = `${account?.usedAiCredits || 0}/${Plans[plan].aiCredits}`;
  const recipientProgressBar = clamp(
    (recipients.length / Plans[plan].recipients) * 100,
    0,
    100
  );

  const userAccountProgressBar = clamp(
    Plans[plan].members === -1
      ? 0
      : ((account?.totalTeamMembers || 0) / Plans[plan].members) * 100,
    0,
    100
  );
  
  const aiCreditsProgressBar = clamp(
    Plans[plan].aiCredits === -1
      ? 0
      : ((account?.usedAiCredits || 0) / Plans[plan].aiCredits) * 100,
    0,
    100
  );
  const livePreviewProgressBar = clamp(
    maxLivePreviewCredits <= 0 || maxLivePreviewCredits === -1
      ? 0
      : (livePreviewCount / maxLivePreviewCredits) * 100,
    0,
    100
  );

  const recipientDescription = `${recipients.length}/${Plans[plan].recipients}`;
  const userAccountInfo =
    Plans[plan].members === -1
      ? `${account?.totalTeamMembers || 0} used (Custom)`
      : `${account?.totalTeamMembers || 0}/${Plans[plan].members}`;

  const exportCount = account?.exportCount || 0;
  const maxExports = Plans[plan]?.exports ?? 10;
  const isUnlimitedExports = maxExports === -1;
  const exportProgressBar = isUnlimitedExports
    ? 0
    : clamp((exportCount / maxExports) * 100, 0, 100);
  const exportDescription = isUnlimitedExports
    ? `${exportCount} used (Unlimited)`
    : `${exportCount}/${maxExports} used (resets 1st of each month)`;
  const livePreviewDescription =
    maxLivePreviewCredits === -1
      ? `${livePreviewCount} used (Custom)`
      : `${livePreviewCount}/${maxLivePreviewCredits} used (resets 1st of each month)`;

  return (
    <div className="space-y-8 mt-6 mb-8 px-4 sm:px-6 md:px-0">
      <Panel>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-base font-semibold tracking-tight text-neutral-900">
              Plans & Pricing
            </h3>
            {/* <p className="mt-1 text-sm text-neutral-500">
                This information will be displayed publicly so be careful what
                you share.
              </p> */}
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="space-y-8">
              <div className="grid grid-cols-3 gap-6">
                {isLoading ? (
                  <Spinner className="h-12 w-12" />
                ) : (
                  <div className="col-span-3 xl:col-span-2">
                    <h4 className="text-base font-bold text-neutral-900 mb-6">
                      {formatCurrentPlanLabel(plan, account?.billingInterval)}
                    </h4>
                    {!isUnlimitedExports && (
                      <div className="my-6">
                        <p className="text-sm font-medium text-neutral-700">
                          Monthly Exports
                        </p>
                        <ProgressBar value={exportProgressBar} />
                        <P>{exportDescription}</P>
                      </div>
                    )}
                    <div className="my-6">
                      <p className="text-sm font-medium text-neutral-700">
                        Test Emails Sent Today
                      </p>
                      <ProgressBar value={previewProgressBar} />
                      <P>{previewDescription} sent (resets midnight PST)</P>
                    </div>
                    <div className="my-6">
                      <p className="text-sm font-medium text-neutral-700">
                        Live Client Preview Credits
                      </p>
                      <ProgressBar value={livePreviewProgressBar} />
                      <P>{livePreviewDescription}</P>
                    </div>
                    <div className="my-6">
                      <p className="text-sm font-medium text-neutral-700">
                        Saved Templates
                      </p>
                      <ProgressBar value={templateProgressBar} />
                      <P>{templateDescription}{!isUnlimitedTemplates && ' used'}</P>
                    </div>

                    {/* <div className="mt-6">
                      <p className="text-sm font-medium text-neutral-700">
                        Test Recipients
                      </p>
                      <ProgressBar value={recipientProgressBar} />
                      <P>{recipientDescription} used</P>
                    </div> */}

                    <div className="mt-6">
                      <p className="text-sm font-medium text-neutral-700">
                        User Accounts
                      </p>
                      <ProgressBar value={userAccountProgressBar} />
                      <P>{userAccountInfo} used</P>
                    </div>
                    <div className="mt-6">
                      <p className="text-sm font-medium text-neutral-700">
                        {`AI Credits (${Plans[plan]?.aiCredits === -1 ? 'Custom' : Plans[plan]?.aiCredits})`}
                      </p>
                      <ProgressBar value={aiCreditsProgressBar} />
                      <P>
                        {Plans[plan]?.aiCredits === -1
                          ? `${account?.usedAiCredits || 0} used (Custom)`
                          : `${usedAiCredits} used (resets 1st of each month)`}
                      </P>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div className="col-span-3 xl:col-span-2 pt-4 border-t border-subtle">
                  <div className="flex justify-start pt-4">
                    <ButtonLink
                      href={plansUrlWithUpgradeSource("account_plan")}
                      onClick={() =>
                        trackUpgradeCtaClicked({
                          source: "account_plan",
                          plan,
                        })
                      }
                    >
                      View Plans
                    </ButtonLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default AccountPlan;
